import {DateTime} from 'luxon';

/**
 * THE ORDER EVERY LEG IS SHOWN AND SENT IN (owner ruling, 2026-08-24).
 *
 * Strike descending, highest at the top - the platform-wide reading direction, the same for
 * Calls and for Puts.
 *
 * STRIKE IS THE LEADING KEY. A box holding two expirations therefore INTERLEAVES them by strike;
 * it does not block by expiration first. Expiration and type only separate legs that sit on the
 * same strike. Confirmed by the owner 2026-08-24 after the opposite reading was put to him.
 *
 *   1. strike      DESC  (highest first)
 *   2. expiration  ASC   (nearest first)
 *   3. type        Calls before Puts
 *   4. original relative order
 *
 * THIS IS NOT A DISPLAY DETAIL. `DropZone.items` is the very array `copyAllOrders()` hands to the
 * copy callback, so this is the order the ticket goes out in, not just the order it is drawn in.
 */
export interface OrderedLeg {
    readonly strike: string;
    readonly expiration: string;
    readonly type: 'Call' | 'Put';
}

/**
 * `strike` reaches us as a STRING (`OrderToRegroupWrapper.strike` returns `strike + ''`), so it
 * has to be parsed. A string compare only looks correct while every strike is four digits and
 * whole: '900' sorts above '1000' lexically, and '77.5' below '77.25'.
 *
 * Anything unparseable sorts to the BOTTOM of a descending list rather than silently claiming the
 * top slot - and, on the right-hand side, with it the "Main" designation.
 */
function strikeOf(leg: OrderedLeg): number {
    const parsed = parseFloat(leg?.strike);
    return isFinite(parsed) ? parsed : Number.NEGATIVE_INFINITY;
}

const FRIENDLY_EXPIRATION = /^\d{2}-[a-zA-Z]{3}-\d{2}$/;

/**
 * Expirations arrive in TWO formats - 'yyyy-MM-dd' and the friendly 'dd-MMM-yy' - and only the
 * first of those sorts correctly as text ('15-Sep-26' compares below '28-Aug-26'). Both are
 * parsed to a timestamp so the comparison is on the date, whichever form it came in.
 *
 * Unparseable sorts LAST in the ascending expiration order, for the same reason as above.
 */
function expirationOf(leg: OrderedLeg): number {
    const raw = leg?.expiration;

    if (!raw) {
        return Number.POSITIVE_INFINITY;
    }

    const dt = FRIENDLY_EXPIRATION.test(raw)
        ? DateTime.fromFormat(raw, 'dd-MMM-yy')
        : DateTime.fromFormat(raw, 'yyyy-MM-dd');

    return dt.isValid ? dt.toMillis() : Number.POSITIVE_INFINITY;
}

function typeRankOf(leg: OrderedLeg): number {
    return leg?.type === 'Put' ? 1 : 0;
}

/**
 * The full key precedence, without the stability tie-break - `sortLegs` supplies that.
 */
export function compareLegs(a: OrderedLeg, b: OrderedLeg): number {

    const strikeDiff = strikeOf(b) - strikeOf(a);
    if (strikeDiff !== 0 && !isNaN(strikeDiff)) {
        return strikeDiff;
    }

    const expirationDiff = expirationOf(a) - expirationOf(b);
    if (expirationDiff !== 0 && !isNaN(expirationDiff)) {
        return expirationDiff;
    }

    return typeRankOf(a) - typeRankOf(b);
}

/**
 * Sort a list of legs IN PLACE into the order above.
 *
 * The original relative order is kept for legs the keys cannot separate. That is done by carrying
 * each leg's starting index rather than by trusting `Array.prototype.sort` to be stable, so the
 * tie-break is visible in the code instead of being a property of the engine.
 */
export function sortLegs<T extends OrderedLeg>(legs: T[]): T[] {

    if (!legs || legs.length < 2) {
        return legs;
    }

    const decorated = legs.map((leg, index) => ({leg, index}));

    decorated.sort((a, b) => compareLegs(a.leg, b.leg) || (a.index - b.index));

    decorated.forEach((entry, index) => legs[index] = entry.leg);

    return legs;
}

/**
 * THE NAME OF A DROP ZONE IS PINNED TO ITS POSITION, NOT TO THE ZONE (owner ruling, 2026-08-24):
 * whichever order ends up holding the highest strike IS "Main", then "Linked #1", and so on.
 *
 * Lives here, next to the sort that decides those positions, because two consumers must agree on
 * it: the Re-group dialog's `reorderDropZones()` and the verification dialog's read-only preview
 * (CR-20260827-001). Two copies of a rule that is re-applied is two chances for the legend and
 * the preview to disagree about a name.
 */
export function zoneTitleForIndex(index: number): string {
    return index === 0 ? 'Main' : `Linked #${index}`;
}

/**
 * The strikes of one box, highest first - the ladder a box is ranked on.
 */
function strikeLadderOf(legs: OrderedLeg[]): number[] {
    return (legs || [])
        .map(strikeOf)
        .filter(strike => strike !== Number.NEGATIVE_INFINITY)
        .sort((a, b) => b - a);
}

/**
 * Rank two boxes by the highest strike each of them holds, descending; a tie is broken on the
 * next-highest strike, and so on down the ladder (owner ruling, 2026-08-24).
 *
 * AN EMPTY BOX RANKS LAST. It has no highest strike to be ranked on, and on the right-hand side
 * the first box is named "Main" - so letting an empty box win the top slot would take that name
 * off an order that has legs in it and give it to one that does not.
 */
function compareStrikeLadders(a: number[], b: number[]): number {

    if (a.length === 0 || b.length === 0) {
        return a.length === b.length ? 0 : (a.length === 0 ? 1 : -1);
    }

    const depth = Math.min(a.length, b.length);

    for (let i = 0; i < depth; i++) {
        if (a[i] !== b[i]) {
            return b[i] - a[i];
        }
    }

    return 0;
}

/**
 * Sort boxes IN PLACE by the highest strike each one holds. Same stability rule as `sortLegs`:
 * boxes the ladder cannot separate keep the order they came in.
 */
export function sortBoxes<T>(boxes: T[], legsOf: (box: T) => OrderedLeg[]): T[] {

    if (!boxes || boxes.length < 2) {
        return boxes;
    }

    const decorated = boxes.map((box, index) => ({box, index, ladder: strikeLadderOf(legsOf(box))}));

    decorated.sort((a, b) => compareStrikeLadders(a.ladder, b.ladder) || (a.index - b.index));

    decorated.forEach((entry, index) => boxes[index] = entry.box);

    return boxes;
}
